import React, { useEffect, useRef, useState, useCallback } from "react";
import { Send, X, RotateCcw, Bot, ChevronRight, ChevronLeft, Pin } from "lucide-react";
import apiClient from "@/api/client";
import { useAuthorization } from "@/auth/AuthorizationContext";
import { toast } from "sonner";

/**
 * BELİ (BelVizyon AI) — Faz 1 yazılı asistan paneli.
 *
 * - Yalnız `beli.kullan` yetkisi olanlarda görünür (aksi halde null).
 * - Açılır floating panel; mevcut ekranları etkilemez (fixed, kendi z-index'i).
 * - Launcher, sağ-alt köşedeki sayfa butonlarını (ör. "Önizle ve Yayınla")
 *   kapatmasın diye: (1) 4 sn etkileşimsiz kalınca yarı saydamlaşır (üzerine
 *   gelince/odaklanınca anında geri döner), (2) küçük ok ile sağ kenara tamamen
 *   gizlenebilir — gizli modda dikey "BELİ" yazan ince bir tutamak kalır ve
 *   tercih localStorage'da saklanır.
 * - Backend POST /api/beli/ask (apiClient → CSRF otomatik).
 * - Sohbet hafızası oturum (session_id) bazlı; "Yeni Sohbet" sıfırlar.
 */

/** Launcher'ın "kenara gizlendi" tercihi (kullanıcı bazlı, tarayıcıda kalıcı). */
const TUCK_KEY = "beli.launcher.tucked";
/** Etkileşim olmadan bu süre geçince launcher yarı saydamlaşır (ms). */
const IDLE_MS = 4000;

function readTucked() {
  try {
    return window.localStorage.getItem(TUCK_KEY) === "1";
  } catch (e) {
    return false;
  }
}

function newSessionId() {
  try {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID().replace(/-/g, "");
  } catch (e) { /* ignore */ }
  return `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

export default function BeliLauncher() {
  const { has, personnelName } = useAuthorization();
  const allowed = typeof has === "function" && has("beli.kullan");

  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(newSessionId);
  const [messages, setMessages] = useState([]); // {role:'user'|'assistant', text, sources?}
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [detayli, setDetayli] = useState(false); // Faz 4-C: "Detaylı Açıklama" görünümü
  const [tucked, setTucked] = useState(readTucked); // sağ kenara gizlendi mi?
  const [idle, setIdle] = useState(false); // etkileşimsiz → yarı saydam
  const scrollRef = useRef(null);
  const idleTimerRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  useEffect(() => {
    try {
      window.localStorage.setItem(TUCK_KEY, tucked ? "1" : "0");
    } catch (e) { /* ignore */ }
  }, [tucked]);

  // Launcher görünürken sayacı işlet: 4 sn dokunulmazsa soluklaş, temas edince uyan.
  const wake = useCallback(() => {
    setIdle(false);
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => setIdle(true), IDLE_MS);
  }, []);

  useEffect(() => {
    if (open || tucked) {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      setIdle(false);
      return undefined;
    }
    wake();
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [open, tucked, wake]);

  const resetChat = useCallback(() => {
    setMessages([]);
    setSessionId(newSessionId());
  }, []);

  const send = useCallback(async () => {
    const q = input.trim();
    if (!q || loading) return;
    setMessages((m) => [...m, { role: "user", text: q }]);
    setInput("");
    setLoading(true);
    try {
      // Faz 4-C: "Detaylı Açıklama" açıkken, cevaba muhakeme izi + kaynaklar + güven
      // seviyesi istenir. Backend salt-okunur açıklama tool'larıyla yanıtlar.
      const message = detayli
        ? `${q}\n\n[Detaylı: cevabını muhakeme izi, kaynaklar ve güven seviyesiyle açıkla.]`
        : q;
      const resp = await apiClient.post(
        "/beli/ask",
        { message, session_id: sessionId },
        // Yerel ücretsiz LLM (Ollama, CPU) yavaş olabilir: 7b model + tool seti tur
        // başına ~75-100 sn sürebilir, çok turlu sorular daha da uzun. Backend LLM
        // timeout'u (BELI_LLM_TIMEOUT, vars. 180 sn) ile hizalı geniş bütçe.
        { timeout: 210000 }
      );
      const d = resp?.data || {};
      setMessages((m) => [
        ...m,
        { role: "assistant", text: d.answer || "—", sources: d.source_modules || [] },
      ]);
    } catch (err) {
      const msg = err?.response?.data?.detail || "BELİ şu an yanıt veremiyor.";
      setMessages((m) => [...m, { role: "assistant", text: String(msg) }]);
      if (err?.response?.status === 403) toast.error("BELİ için yetkiniz bulunmuyor.");
    } finally {
      setLoading(false);
    }
  }, [input, loading, sessionId, detayli]);

  if (!allowed) return null;

  return (
    <>
      {/* Floating buton — kompakt ikon + "kenara gizle" tutamağı.
          Etiket kaldırıldığı için altındaki sayfa butonlarını çok daha az kapatır. */}
      {!open && !tucked && (
        <div
          className={`fixed bottom-5 right-5 z-[9990] transition-opacity duration-500 hover:opacity-100 focus-within:opacity-100 ${
            idle ? "opacity-40" : "opacity-100"
          }`}
          onMouseEnter={wake}
          onFocus={wake}
        >
          <button
            type="button"
            onClick={() => setOpen(true)}
            title="BELİ — BelVizyon Asistanı"
            aria-label="BELİ asistanını aç"
            className="flex h-11 items-center gap-2 rounded-full bg-green-700 hover:bg-green-800 text-white pl-3 pr-4 shadow-lg"
          >
            <Bot className="h-5 w-5 shrink-0" />
            <span className="text-sm font-semibold leading-none">BELİ</span>
          </button>
          <button
            type="button"
            onClick={() => { setTucked(true); setIdle(false); }}
            title="BELİ butonunu sağ kenara gizle"
            aria-label="BELİ butonunu sağ kenara gizle"
            className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-500 shadow hover:bg-gray-100 hover:text-gray-700"
          >
            <ChevronRight className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* Gizli mod — sağ kenarda ince tutamak; köşeyi tamamen boşaltır.
          İki ayrı düğme: üstteki ok butonu köşeye GERİ GETİRİR, alttaki "BELİ"
          şeridi sohbeti açar (gizliyken de asistan tek tıkla kullanılabilir). */}
      {!open && tucked && (
        <div className="fixed right-0 bottom-28 z-[9990] flex w-7 flex-col items-center overflow-hidden rounded-l-md bg-green-700/80 text-white shadow-md">
          <button
            type="button"
            onClick={() => { setTucked(false); }}
            title="BELİ butonunu köşeye geri getir"
            aria-label="BELİ butonunu köşeye geri getir"
            className="flex h-6 w-full items-center justify-center border-b border-white/30 hover:bg-green-800"
          >
            <ChevronLeft className="h-3.5 w-3.5 shrink-0" />
          </button>
          <button
            type="button"
            onClick={() => setOpen(true)}
            title="BELİ'yi aç"
            aria-label="BELİ asistanını aç"
            className="flex w-full flex-1 items-center justify-center py-2 hover:bg-green-800"
          >
            <span className="text-[11px] font-semibold tracking-wide" style={{ writingMode: "vertical-rl" }}>
              BELİ
            </span>
          </button>
        </div>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed bottom-5 right-5 z-[9990] w-[360px] max-w-[92vw] h-[520px] max-h-[80vh] bg-white border rounded-xl shadow-2xl flex flex-col overflow-hidden">
          {/* Başlık */}
          <div className="flex items-center justify-between px-4 py-3 bg-green-700 text-white">
            <div className="flex items-center gap-2">
              <Bot className="h-5 w-5" />
              <div className="leading-tight">
                <div className="text-sm font-semibold">BELİ</div>
                <div className="text-[11px] opacity-80">BelVizyon Asistanı</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {tucked && (
                <button type="button" title="BELİ butonunu köşeye geri getir"
                  aria-label="BELİ butonunu köşeye geri getir"
                  onClick={() => setTucked(false)}
                  className="p-1.5 rounded hover:bg-green-600">
                  <Pin className="h-4 w-4" />
                </button>
              )}
              <button type="button" title="Yeni Sohbet" onClick={resetChat}
                className="p-1.5 rounded hover:bg-green-600">
                <RotateCcw className="h-4 w-4" />
              </button>
              <button type="button" title="Kapat" onClick={() => setOpen(false)}
                className="p-1.5 rounded hover:bg-green-600">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Mesajlar */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-gray-50">
            {messages.length === 0 && (
              <div className="text-xs text-gray-500 leading-relaxed">
                Merhaba {personnelName || ""}. BelVizyon ile ilgili sorularınızı yanıtlarım
                (personel, park, bölge, izin, bildirim, konum; modül rehberleri; modüller arası
                etki ve KVKK açıklamaları). Yalnız bilgi veririm; karar vermem, işlem yapmam,
                personel seçmem/değerlendirmem. Sadece kendi yetki alanınızdaki bilgilere erişirim.
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm whitespace-pre-wrap ${
                  m.role === "user" ? "bg-green-600 text-white" : "bg-white border text-gray-800"
                }`}>
                  {m.text}
                  {m.role === "assistant" && Array.isArray(m.sources) && m.sources.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {m.sources.map((s, j) => (
                        <span key={j} className="text-[10px] bg-gray-100 text-gray-600 border rounded px-1.5 py-0.5">
                          Kaynak: {s}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border text-gray-500 text-sm px-3 py-2 rounded-lg">BELİ yazıyor…</div>
              </div>
            )}
          </div>

          {/* Girdi */}
          <div className="border-t p-2 bg-white">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                rows={1}
                placeholder="BelVizyon ile ilgili sorunuzu yazın…"
                disabled={loading}
                className="flex-1 resize-none border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500 max-h-28"
              />
              <button
                type="button"
                onClick={send}
                disabled={loading || !input.trim()}
                className="shrink-0 rounded-md bg-green-700 hover:bg-green-800 disabled:opacity-50 text-white p-2"
                title="Gönder"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-1 flex items-center justify-between">
              <label className="flex items-center gap-1.5 text-[10px] text-gray-500 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={detayli}
                  onChange={(e) => setDetayli(e.target.checked)}
                  className="h-3 w-3 accent-green-700"
                />
                Detaylı Açıklama (muhakeme + kaynak + güven)
              </label>
            </div>
            <div className="mt-1 text-[10px] text-gray-400">
              BELİ yalnız bilgi verir; işlem yapmaz. İşlemler ilgili modülden yapılır.
            </div>
          </div>
        </div>
      )}
    </>
  );
}
